import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../App";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BookingModal from "../components/BookingModal";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Wind, Waves, ArrowRight } from "lucide-react";

const LessonsPage = () => {
  const { language } = useLanguage();
  const [bookingModalOpen, setBookingModalOpen] = useState(false);

  const categories = [
    {
      id: "kitesurf",
      slug: "kitesurf-lessons",
      name: { en: "Kitesurf Lessons", es: "Lecciones de Kitesurf", de: "Kitesurf-Unterricht", fr: "Cours de Kitesurf" },
      description: {
        en: "Professional IKO certified instruction for all levels. From beginner to advanced freestyle — learn with the best on Dakhla lagoon.",
        es: "Instruccion profesional certificada IKO para todos los niveles. De principiante a freestyle avanzado.",
        de: "Professioneller IKO-zertifizierter Unterricht fur alle Levels. Vom Anfanger bis zum Freestyle.",
        fr: "Instruction professionnelle certifiee IKO pour tous niveaux. Du debutant au freestyle avance."
      },
      icon: Wind,
      image: "https://customer-assets.emergentagent.com/job_53c07cd7-1ab5-4cff-8752-32dcf9dc74c8/artifacts/ivcxbnxg_IMG_3878.jpeg",
      color: "from-blue-600 to-cyan-500",
      fromPrice: 200,
    },
    {
      id: "surf",
      slug: "surf-lessons",
      name: { en: "Surf Courses", es: "Cursos de Surf", de: "Surf-Kurse", fr: "Cours de Surf" },
      description: {
        en: "Catch your first wave in Dakhla! Group and private lessons with all gear included on the best Atlantic swells.",
        es: "Atrapa tu primera ola en Dakhla! Clases grupales y privadas con todo el equipo incluido.",
        de: "Fange deine erste Welle in Dakhla! Gruppen- und Privatunterricht mit Ausrustung.",
        fr: "Attrapez votre premiere vague a Dakhla! Cours en groupe et prives avec equipement inclus."
      },
      icon: Waves,
      image: "https://customer-assets.emergentagent.com/job_acc6b5c6-4c76-49ba-a1fd-498cb047a8ad/artifacts/7txvwmsl_IMG_4362.jpeg",
      color: "from-orange-500 to-yellow-500",
      fromPrice: 25,
    },
    {
      id: "wingfoil",
      slug: "wingfoil-lessons",
      name: { en: "Wing Foil Lessons", es: "Lecciones de Wing Foil", de: "Wing Foil Unterricht", fr: "Cours de Wing Foil" },
      description: {
        en: "Master the newest water sport! Professional wingfoil coaching from semi-private to private sessions in Dakhla.",
        es: "Domina el deporte acuatico mas nuevo! Coaching profesional de wingfoil en Dakhla.",
        de: "Meistere den neuesten Wassersport! Professionelles Wingfoil-Coaching in Dakhla.",
        fr: "Maitrisez le sport nautique le plus recent! Coaching wingfoil professionnel a Dakhla."
      },
      icon: Wind,
      image: "https://images.unsplash.com/photo-1666175497168-48b72df461cf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHx3aW5nZm9pbCUyMHdpbmclMjBmb2lsJTIwd2F0ZXIlMjBzcG9ydHxlbnwwfHx8fDE3NzM4ODY5MTJ8MA&ixlib=rb-4.1.0&q=85",
      color: "from-purple-600 to-pink-600",
      fromPrice: 180,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header onBookingClick={() => setBookingModalOpen(true)} />

      {/* Hero */}
      <section className="pt-28 pb-16 bg-gradient-to-b from-sky-950 to-sky-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="lessons-page-title">
            {language === "es" ? "Nuestras Lecciones" : language === "de" ? "Unsere Kurse" : language === "fr" ? "Nos Cours" : "Our Lessons"}
          </h1>
          <p className="text-lg text-sky-200 max-w-2xl mx-auto">
            {language === "es" ? "Elige tu aventura acuatica en Dakhla con instructores certificados IKO" :
             language === "de" ? "Wahle dein Wasserabenteuer in Dakhla mit IKO-zertifizierten Lehrern" :
             language === "fr" ? "Choisissez votre aventure aquatique a Dakhla avec des instructeurs certifies IKO" :
             "Choose your water adventure in Dakhla with IKO certified instructors"}
          </p>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 -mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {categories.map((cat) => (
              <Link key={cat.id} to={`/lessons/${cat.slug}`} data-testid={`lesson-card-${cat.id}`}>
                <Card className="group overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-2 h-full">
                  <div className="h-56 bg-cover bg-center relative" style={{ backgroundImage: `url(${cat.image})` }}>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                    <div className="absolute bottom-4 left-5 right-5">
                      <div className={`w-10 h-10 bg-gradient-to-r ${cat.color} rounded-full flex items-center justify-center mb-2`}>
                        <cat.icon className="h-5 w-5 text-white" />
                      </div>
                      <h2 className="text-2xl font-bold text-white">{cat.name[language]}</h2>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <p className="text-gray-600 text-sm mb-4 leading-relaxed">{cat.description[language]}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">
                        {language === "fr" ? "A partir de" : language === "es" ? "Desde" : language === "de" ? "Ab" : "From"} <span className="text-lg font-bold text-gray-900">{cat.fromPrice}€</span>
                      </span>
                      <span className={`inline-flex items-center gap-1 text-sm font-medium bg-gradient-to-r ${cat.color} bg-clip-text text-transparent group-hover:gap-2 transition-all`}>
                        {language === "es" ? "Ver opciones" : language === "de" ? "Optionen" : language === "fr" ? "Voir les options" : "View options"}
                        <ArrowRight className="h-4 w-4 text-blue-500" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <BookingModal isOpen={bookingModalOpen} onClose={() => setBookingModalOpen(false)} />
    </div>
  );
};

export default LessonsPage;
